// File: src/theme/ThemeProvider.tsx
// Purpose: Theme and styling-related file

import React, { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { lightTheme, darkTheme } from "./themes";

interface ThemeContextType {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: "light" | "dark";
  customTheme?: DefaultTheme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, defaultTheme = "light", customTheme }) => {
  const [theme, setTheme] = useState<"light" | "dark">(defaultTheme);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme") as "light" | "dark";
    if (storedTheme) setTheme(storedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const selectedTheme = customTheme || (theme === "light" ? lightTheme : darkTheme);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      <StyledThemeProvider theme={selectedTheme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};